import React from "react";
import { ChevronRight, Loader2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { RiskFlag, RiskFlagCategory } from "@/lib/api/risk-flagging";
import { useSignedMedia } from "@/lib/hooks/use-signed-media";
import { getMenteeDisplayName } from "@/lib/redacted-display";
import { isS3Url, getMediaType } from "./utils";
import { PriorityPill } from "./pills";
import { CATEGORY_LABELS } from "./constants";

export function DrawerField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-xs text-[#58534c]">{label}</p>
      <div className="text-sm text-[#1f1e1d]">{children}</div>
    </div>
  );
}

export function Divider() {
  return <div className="h-px w-full bg-[rgba(31,30,29,0.12)]" />;
}

export function AlertInfo({ alert }: { alert: RiskFlag }) {
  const isSurvey = alert.sourceType === "survey";
  const uniqueCategories = [...new Set(alert.categories?.map(c => c.category) ?? [])];
  const teen = alert.mentorship?.mentee ?? alert.user;
  const teenName = teen ? getMenteeDisplayName(teen) : "Unknown";
  return (
    <div className="flex flex-col gap-4">
      <DrawerField label="Teen">{teenName}</DrawerField>
      {isSurvey ? (
        <DrawerField label="Source">
          <span className="inline-flex items-center rounded-full bg-[#f0edfb] px-2 py-1 text-sm text-[#5b4fa0]">
            Survey response
          </span>
        </DrawerField>
      ) : (
        <DrawerField label="Mentor">
          {alert.mentorship?.mentor?.name ?? "Unknown"}
        </DrawerField>
      )}
      <DrawerField label="Priority">
        <PriorityPill priority={alert.priority} />
      </DrawerField>
      {uniqueCategories.length > 0 && (
        <DrawerField label="Categories">
          <div className="flex flex-wrap gap-1.5">
            {uniqueCategories.map((cat) => (
              <span key={cat} className="inline-flex items-center rounded-full bg-[#ece8e1] px-2 py-1 text-sm text-[#1f1e1d]">
                {CATEGORY_LABELS[cat] ?? cat}
              </span>
            ))}
          </div>
        </DrawerField>
      )}
      <DrawerField label="Detected">
        {formatDistanceToNow(new Date(alert.created_at), { addSuffix: true })}
      </DrawerField>
    </div>
  );
}

function MediaPreview({ url }: { url: string }) {
  const { url: signedUrl, isPending } = useSignedMedia(url);
  const mediaType = getMediaType(url);

  if (isPending) {
    return (
      <div className="flex h-24 items-center justify-center rounded-lg bg-zinc-50">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (!signedUrl || !mediaType) return null;

  if (mediaType === "image") {
    return (
      <img
        src={signedUrl}
        alt="Flagged content"
        className="max-h-48 w-full rounded-lg object-contain"
      />
    );
  }

  if (mediaType === "video") {
    return (
      <video src={signedUrl} controls className="max-h-48 w-full rounded-lg">
        Your browser does not support the video tag.
      </video>
    );
  }

  if (mediaType === "audio") {
    return <audio src={signedUrl} controls className="w-full" />;
  }

  return null;
}

function SurveyTriggers({ alert }: { alert: RiskFlag }) {
  const triggers = (alert.metadata?.triggers as string[] | undefined) ??
    (alert.metadata?.trigger ? [alert.metadata.trigger as string] : []);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-[#58534c]">Survey triggers</p>
      {triggers.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          {triggers.map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-red-400" />
              <p className="text-sm font-medium text-[#1f1e1d]">{t}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm italic text-[#58534c]">No trigger details</p>
      )}
    </div>
  );
}

export function FlaggedContent({
  alert,
  categories,
  onViewContext,
}: {
  alert: RiskFlag;
  categories: RiskFlagCategory[];
  onViewContext: () => void;
}) {
  const isSurvey = alert.sourceType === "survey";
  const content = alert.sourceContent ?? alert.transcribedText;
  const isMedia = content ? isS3Url(content) : false;

  return (
    <div className="flex flex-col gap-4">
      {isSurvey ? (
        <SurveyTriggers alert={alert} />
      ) : (
        <>
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <p className="text-xs text-[#58534c]">Flagged content</p>
            </div>
            {isMedia ? (
              <div className="mt-1" onClick={(e) => e.stopPropagation()}>
                <MediaPreview url={content!} />
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="w-0.5 self-stretch rounded-full bg-[#1f1e1d]" />
                <p className="text-sm leading-5 tracking-[-0.15px] text-[#1f1e1d]">
                  {content ? (
                    `"${content}"`
                  ) : (
                    <span className="italic text-[#58534c]">Content not available</span>
                  )}
                </p>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onViewContext}
            className="flex items-center gap-1.5 text-xs text-[#58534c] hover:text-[#1f1e1d] transition-colors w-fit"
          >
            <ChevronRight className="h-3 w-3" />
            <span className="underline underline-offset-2">View chat context</span>
          </button>
          {alert.transcribedText && isMedia && (
            <DrawerField label="Transcription">
              <p className="text-sm leading-5 tracking-[-0.15px] text-[#1f1e1d]">
                "{alert.transcribedText}"
              </p>
            </DrawerField>
          )}
        </>
      )}
      <DrawerField label="Why this was flagged">
        {categories.length === 1 ? (
          <p className="text-sm leading-5">{categories[0].explanation}</p>
        ) : (
          <div className="flex flex-col gap-3">
            {categories.map((cat, i) => (
              <div key={i}>
                <p className="text-xs font-medium text-[#58534c] mb-0.5">
                  {CATEGORY_LABELS[cat.category] ?? cat.category}
                </p>
                <p className="text-sm leading-5">{cat.explanation}</p>
              </div>
            ))}
          </div>
        )}
      </DrawerField>
    </div>
  );
}

export function ClosedFeedback({ alert }: { alert: RiskFlag }) {
  if (alert.resolutionReason === "expired") {
    return (
      <div className="flex flex-col gap-3.5">
        <DrawerField label="Resolution">
          <span className="text-sm text-zinc-500">Auto-expired after 7 days</span>
        </DrawerField>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3.5">
      <DrawerField label="Flagged correctly">
        {alert.feedbackCorrect === true ? "Yes" : alert.feedbackCorrect === false ? "No" : "—"}
      </DrawerField>
      {alert.feedbackNote && (
        <DrawerField label="Reviewer notes">{alert.feedbackNote}</DrawerField>
      )}
      {alert.closedByUser && (
        <DrawerField label="Reviewed by">{alert.closedByUser.name}</DrawerField>
      )}
    </div>
  );
}

export function FeedbackPrompt({ onSelect }: { onSelect: (correct: boolean) => void }) {
  return (
    <div className="flex flex-col gap-3.5">
      <p className="text-xs text-[#58534c]">Was this flagged correctly?</p>
      <div className="flex gap-4">
        <button
          onClick={() => onSelect(false)}
          className="flex-1 rounded-[10px] border border-[#ece8e1] bg-white px-10 py-1.5 text-sm leading-5 tracking-[-0.15px] text-[#1f1e1d] hover:bg-neutral-50"
        >
          No
        </button>
        <button
          onClick={() => onSelect(true)}
          className="flex-1 rounded-[10px] bg-black px-10 py-1.5 text-sm leading-5 tracking-[-0.15px] text-white hover:bg-zinc-800"
        >
          Yes
        </button>
      </div>
    </div>
  );
}

export function ResolveForm({
  feedbackCorrect,
  feedbackNote,
  isPending,
  onChangeCorrect,
  onChangeNote,
  onResolve,
}: {
  feedbackCorrect: boolean;
  feedbackNote: string;
  isPending: boolean;
  onChangeCorrect: (val: boolean | null) => void;
  onChangeNote: (val: string) => void;
  onResolve: () => void;
}) {
  return (
    <div className="flex flex-1 flex-col gap-3.5">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#58534c]">
          Flagged correctly:{" "}
          <span className="font-medium text-[#1f1e1d]">
            {feedbackCorrect ? "Yes" : "No"}
          </span>
        </p>
        <button
          onClick={() => onChangeCorrect(null)}
          className="text-xs text-[#58534c] underline hover:text-[#1f1e1d]"
        >
          Change
        </button>
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-xs text-[#58534c]">Tell us more</p>
        <textarea
          value={feedbackNote}
          onChange={(e) => onChangeNote(e.target.value)}
          placeholder="eg. This was flagged correctly..."
          className="h-[140px] w-full resize-none rounded-[10px] border border-[#ece8e1] bg-white px-4 py-3 text-sm leading-5 text-[#1f1e1d] placeholder:text-[#a09a93] focus:outline-none focus:ring-1 focus:ring-[#ece8e1]"
        />
      </div>
      <div className="flex justify-end">
        <button
          onClick={onResolve}
          disabled={isPending}
          className="w-[163.5px] rounded-[10px] bg-black py-1.5 text-sm font-medium text-white hover:bg-zinc-800 disabled:opacity-50"
        >
          {isPending ? "Resolving..." : "Resolve"}
        </button>
      </div>
    </div>
  );
}
